// WHY a `pending` task is not moving, DERIVED at read time — the composed
// queue-stall report. See `../task.ts` for the file docblock.

import { z } from 'zod';
import type { TaskId } from '../../value-objects/ids';
import type { TaskOrigin } from '../../value-objects/task-envelope/origin';
import { ONBOARDING_BRANCH } from '../../value-objects/task-run-event/onboarding';
import { ENV_FAULT_HOLD_REMEDY } from './env-fault-hold';
import { BOX_FAULT_EXCLUSION_MINUTES } from './failure-budget';
import type { HoldWindowDetail } from './hold-window';
import { type ResumeBackoffView, resumeBackoffRemedy } from './resume-backoff';

// ─── why is nothing moving (arc 101 slice 11 Part C) ─────────────────────────

/**
 * WHY A PENDING TASK IS NOT MOVING — the composed vocabulary behind the ONE
 * call that answers *"why is nothing happening"* (`queueStallReport`,
 * `@developerz/db`).
 *
 * It is not a second {@link TaskHoldReason}. That column is what a dispatcher
 * PASS stamped, and it is null on every pending row that no pass ever reached —
 * which is the defect, not an edge case. Measured on prod 2026-08-24: **ten
 * pending tasks, `hold_reason` NULL on every one, the oldest thirty-three days
 * old**, across two accounts with two entirely different and entirely knowable
 * causes (one account owns no box at all; the other's four rows sit behind a
 * plan group that will never merge). Both facts were joinable in one query the
 * whole time and no call returned them.
 *
 * So these kinds are DERIVED at read time from the same rows the claim gate
 * reads, not read back off a stamp. A reason that only exists once something
 * wrote it cannot explain a queue nothing has looked at.
 *
 * `queued` is deliberately NOT a member. A task waiting its turn behind other
 * work is not held — the queue depth already says that, and a report that lists
 * "busy" as a blocker has no signal left. The classifier answers
 * `TaskStallKind | null`, and `null` IS "genuinely just busy".
 */
export const TaskStallKind = {
  /**
   * A `depends_on` target reached a terminal status that will never satisfy
   * ({@link TASK_DEP_UNSATISFIABLE_STATUSES}). Permanent — no retry of this row
   * can ever clear it. Derived from the DAG rather than read off a stored
   * column, so a row is explained the moment its dependency dies rather than
   * when the sweep next runs.
   *
   * That choice is why this vocabulary survived and `TaskHoldReason`'s
   * same-named member did not: deriving from the DAG needs no writer and no
   * reader to agree, while the stored twin was written onto a `failed` row that
   * {@link TaskFailureKind.DependencyFailed} already explained, and could not be
   * read back through `normalizeTask` at all.
   */
  DependencyFailed: 'dependency_failed',
  /**
   * A human answer is owed — an open `blockers` row (slice 11 Part B).
   *
   * The ONE kind whose unit is not always a pending task, and it has to be: a
   * SCOUT blocker holds no task at all, because the scout authored no plan and
   * `apps/worker/src/fleet/blocker-ingest.ts` writes `task_id: null` on purpose.
   * Keyed on the task alone, this arm was unreachable for every ask prod had
   * ever raised — 80 open blockers on 2026-08-30, 79 of them `task_id IS NULL`
   * with a `run_id`, the 80th keyed to a `failed` task, and the report answered
   * "nothing is held" to all of them. So `queueStallReport` counts held WORK: a
   * pending row a blocker holds, or the run behind an ask no pending row carries.
   */
  BlockedOnAnswer: 'blocked_on_answer',
  /**
   * The stacked-PR / cross-repo gate: this task is a plan group's dispatched
   * task and an UPSTREAM group is not `merged`. The gate that held four
   * developerz-ai rows for ten days with `hold_reason` NULL — the upstream
   * group's own task was `completed`, its PR never merged, and nothing in the
   * platform could say so.
   */
  UpstreamGroupUnmerged: 'upstream_group_unmerged',
  /**
   * The deploy gate of a `deployed` plan edge (plan 101-multi-repo-goal-legs D3): the
   * upstream group MERGED, and its merge commit is not yet live in the environment the edge
   * names (no `success` as the newest verdict there). Derived from `upstreamLegUndeployedSql`.
   */
  UpstreamLegUndeployed: 'upstream_leg_undeployed',
  /**
   * A `depends_on` target has not released the row. Usually a legitimate, bounded wait on
   * work in flight — but NOT when the target waits back on this row: a dependency CYCLE is
   * neither, and only the dependency sweep (an older→newer edge on the cycle) or an operator
   * cancelling one side ends it. See the remedy.
   */
  DependencyPending: 'dependency_pending',
  /**
   * A `depends_on` target will never release this row BY ITSELF, and is not dead either,
   * so neither `dependency_failed` nor `dependency_pending` is true of it:
   *
   *   - a PR CHORE (`ORIGIN_PR_CHORE_SPEC`: a babysit round, a review, a review reply) —
   *     it works on a PR another task owns, never merges and never carries that PR;
   *   - a task a regroup SUPERSEDED — its slot moved to its replacements;
   *   - a `pr` task that COMPLETED without the pull request it promised
   *     (`pr_unfulfilled`, no no-work settlement) — merged is now unreachable unless a
   *     PR is linked late.
   *
   * The first two are the dependency sweep's heal (`healSupersededDependencies`,
   * `@developerz/db`), which rewrites the edge within a tick; the third waits on a person.
   * Until 2026-09-30 all three read as `dependency_pending` ("let it finish") about an
   * upstream that had finished — prod tsk_0f2a2c72… (tesote/tesote.ai) sat behind a
   * completed babysit round of a PR that had closed unmerged, with seven tasks behind it.
   */
  DependencyUnreleasable: 'dependency_unreleasable',
  /** This account owns no live box, so nothing of its own can ever claim the row. */
  NoBoxOnAccount: 'no_box_on_account',
  /** The account has a key pool and zero usable keys ({@link TaskHoldReason.NoUsableKeys}). */
  NoUsableKeys: 'no_usable_keys',
  /** The #513 same-area gate: a non-`parallel_safe` sibling holds this `(account, repo)`. */
  AreaBusy: 'area_busy',
  /**
   * The repo has never been onboarded (`repositories.ai_first_at IS NULL`), so
   * the claim gate holds every `outcome_kind = 'pr'` task on it —
   * `docs/idea/repo-onboarding.md`. Coding work ONLY: triage, PR review,
   * releases, `report` and `ops_action` outcomes are untouched, because none of
   * them edits the repo the pass exists to prepare.
   *
   * Permanent until somebody acts, which is why it outranks the account-wide
   * arms below it: an account with no box gets one eventually, while an
   * un-onboarded repo stays un-onboarded until the `dz/onboarding` PR merges,
   * the pass returns `ready`, or a maintainer declares it.
   */
  RepoNotOnboarded: 'repo_not_onboarded',
  /**
   * The task is an INTERACTIVE session (`scout` / `setup`) and the account holds
   * no ONLINE box that can run one. Only `universal` can
   * ({@link runnerRoleCanConverse}), so an account whose whole fleet is pinned
   * `worker` has coding capacity and no conversational capacity at all.
   *
   * DERIVED, not read off {@link TaskHoldReason.NoConverseCapacity}: both inputs
   * are stored rows (the task's origin, the account's runners), so the report can
   * answer for a pending row no dispatcher pass ever reached -- which is the whole
   * reason this vocabulary exists beside the column.
   */
  NoConverseCapacity: 'no_converse_capacity',
  /**
   * Coding work on a repo that declares a `services:` block (#2919), where no ONLINE
   * box on the account adverts one of the `service:*` tokens that declaration derives.
   * PLUS the `no_capable_box` fold (`TASK_HOLD_STALL`, #2148): a task whose own
   * `requires` token no online box advertises classifies here too — of any origin,
   * often with no `services:` block at all; the token gate is the services gate's
   * superset, and the remedy below carries the token case's general action.
   *
   * DERIVED, not read off {@link TaskHoldReason.NoServiceCapacity}, for the reason
   * {@link NoConverseCapacity} gives: both inputs are stored rows (the repo's active
   * policy, the account's runners), so the report answers for a pending row no
   * dispatcher pass ever reached — which is the whole reason this vocabulary exists
   * beside the column. The folded half is the exception: read off the stamp, and
   * stamp-only — `markNoCapableBoxHolds` admits no window (`tasks_hold_until_ck`
   * refuses one), and the sweep retracts its own verdict when a capable box enrolls.
   */
  NoServiceCapacity: 'no_service_capacity',
  /**
   * The account is at its AI-dev cap (#3630): every `entitlements().aiDevs` slot
   * is running, so the assigner held this row rather than claim it.
   *
   * READ OFF THE STAMP ({@link TaskHoldReason.SeatCapped}), like `NoUsableKeys`
   * and for the same reason: whether the account's slots are all taken is
   * resolved by the assigner's concurrency gate over the live claim set, which no
   * row here can reproduce. Before this member a seat-capped queue reported as
   * plain `queued` — the one hold whose remedy is a seat or an `ai_devs` grant,
   * reported as "wait".
   */
  SeatCapped: 'seat_capped',
  /**
   * The account's shared BYOK router pool refused one of its boxes for a
   * capacity/rate limit and the router dated the reset, so the assigner steps aside
   * from claiming for that account until it passes
   * ({@link TaskHoldReason.PoolCooling}).
   *
   * READ OFF THE STAMP, the THIRD arm that is — `NoUsableKeys` and `SeatCapped` are
   * the other two — and for the reason both of those give, in its sharpest form: the
   * fact does not live in Postgres AT ALL. It is a Dragonfly record with a TTL
   * (`account_backend_cooldown:<accountId>`, written by `coolBackend` off the
   * router's own quoted reset), so there is no row here to derive it from and a
   * derived predicate would have to invent one. This vocabulary DERIVES wherever its
   * inputs are stored rows precisely so a pending row no pass reached is still
   * explained; where the input is not a row, the honest arm reads the stamp, and
   * saying which is which is the whole discipline.
   *
   * The consequence, stated rather than left to be discovered: a row NO pass ever
   * reached carries no stamp, so this kind cannot name it. That population is the
   * one `NoBoxOnAccount` and the derived arms answer, and it is the price of the
   * fact being off-database — not a gap this kind could close by deriving harder.
   */
  PoolCooling: 'pool_cooling',
  /**
   * The resolver PARKED this task until `hold_until`: every pool key that serves it is
   * cooling — rate-limited, or out of quota until a stated reset — and none that is
   * live does ({@link TaskHoldReason.CredentialCooling}, `CredentialCoolingHold`).
   *
   * READ OFF THE STAMP, and only while its window is live: the verdict needs the task
   * in hand (lane fence, tier, the cooling keys' Dragonfly TTLs), so no stored row
   * re-derives it, and a lapsed `hold_until` is a row the next claim re-decides.
   */
  CredentialCooling: 'credential_cooling',
  /**
   * A box is kept off this task for a fault of its own, and no other box has claimed
   * it yet. Three conditions: the box that last refused it for a red baseline is
   * excluded for `BOX_FAULT_EXCLUSION_MINUTES` (#3094), a box could not decode its
   * assignment and it waits for a box on a newer runner release (#6237), or its gate
   * outgrew a box's per-task memory cap and no online box serving the account has a larger
   * one (#7081, `./gate-memory-cap.ts`).
   *
   * DERIVED, not read off {@link TaskHoldReason.BoxFaultExcluded}: each condition is
   * stored on the task row (`box_fault_runner_ids` + `box_fault_until`;
   * `decode_refused_release_at`; `memory_unmet_cap_bytes` beside the boxes' sampled caps),
   * so `stallPredicateSql` computes this kind from the fragments the claim gate reads
   * (`@developerz/db` `queries/tasks/stalls.ts`), so the report and the claim cannot disagree.
   */
  BoxFaultExcluded: 'box_fault_excluded',
  /**
   * The task's agent mode is switched OFF for its repo (plan 101 slice 06) — an
   * operator's deliberate choice on the dashboard, API or MCP.
   *
   * DERIVED, not read off {@link TaskHoldReason.ModeDisabled}: both inputs are stored
   * rows (the task's origin, the `agent_modes` rows), so `stallPredicateSql` computes
   * it from the same `modeDisabledSql()` fragment the claim gate and the hold writer
   * use — the report, the column and the claim cannot disagree.
   */
  ModeDisabled: 'mode_disabled',
  /**
   * The repo's own `bin/setup` is down on every box — a repo-environment fault
   * (`./repo-env-fault`) — so this setup-running row is parked on
   * {@link TaskHoldReason.RepoEnvFault} until a probe passes setup.
   *
   * READ OFF THE STAMP, and the stamp is the gate: the claim skips the row on its
   * `hold_until` window, so "stamped and the window is live" is exactly "the claim
   * refuses it". A derived arm over the fault row would disagree with the claim on the
   * probe the sweep released and on a row minted since its last tick.
   */
  RepoEnvFault: 'repo_env_fault',
  /**
   * A coding task spent its attempts with work saved, so it is PARKED on
   * {@link TaskHoldReason.ResumeBackoff} and resumes that work on its own after a backoff
   * (`./resume-backoff`). Read off the stamp and its live window, like {@link RepoEnvFault}.
   */
  ResumeBackoff: 'resume_backoff',
  /**
   * Every pool key that could serve this task is at its concurrency capacity, so the
   * resolver PARKED it until `hold_until` — a short re-check — rather than start one
   * more model stream on a plan that cannot run it ({@link TaskHoldReason.KeySaturated}).
   *
   * READ OFF THE STAMP, and only while its window is live, for {@link CredentialCooling}'s
   * reason: the verdict needs the task in hand (which keys serve its lane and tier) and
   * each key's in-flight units, which are claim-time Dragonfly state, so no stored row
   * re-derives it. A lapsed `hold_until` is a row the next claim re-decides.
   */
  KeySaturated: 'key_saturated',
  /**
   * The task's lane is bound to an Agents-page SELECTION whose key cannot run now —
   * cooling, stepped aside after a refusal, or refusing run after run — so the resolver
   * PARKED it on {@link TaskHoldReason.SelectionWaiting} until `hold_until` rather than
   * hand it to a key the page did not name. Other keys on the account may be idle: this
   * is the owner's selection waiting, never an account key outage.
   *
   * READ OFF THE STAMP, and only while its window is live, for {@link CredentialCooling}'s
   * reason: the verdict needs the task in hand and the selected key's runtime state.
   */
  SelectionWaiting: 'selection_waiting',
  /**
   * A person is pushing to this babysit round's pull request, so the round is PARKED on
   * {@link TaskHoldReason.PrQuietWindow} until the branch has been quiet for the window
   * (`../babysit/human-quiet`). Read off the stamp and its live window, like
   * {@link ResumeBackoff}.
   */
  PrQuietWindow: 'pr_quiet_window',
  /**
   * The task spent its attempt ladder on environment faults that never graded the change,
   * so it is HELD on {@link TaskHoldReason.EnvFaultExhausted} with an open blocker instead
   * of failed (`./env-fault-hold`). Read off the stamp and its live window, like
   * {@link ResumeBackoff}: the failure write's hold step is its only writer.
   */
  EnvFaultExhausted: 'env_fault_exhausted',
  /**
   * Every machine that could run this task has its role switched OFF
   * ({@link TaskHoldReason.NoRoleMachine}, #6552 "roles on machines").
   *
   * Derived from `noRoleMachineSql()`, the fragment the hold writer (`markNoRoleMachineHolds`)
   * stamps from; the claim enforces its box-local half, `machineRoleOffSql`. No window
   * conjunct: the writer stamps no `hold_until`.
   */
  NoRoleMachine: 'no_role_machine',
  /**
   * A person paused this task's epic ({@link TaskHoldReason.PlanPaused}, #6748): its plan
   * group belongs to a plan with `plans.paused_at` set, and pausing an epic holds its waiting
   * slices with the running ones. Derived from `planPausedSql()`, the fragment the claim
   * narrows with and `markPlanPausedHolds` stamps from. Released only by Resume.
   */
  PlanPaused: 'plan_paused',
  /**
   * The task is pinned to the machine that saved its paused work
   * ({@link TaskHoldReason.MachinePinned}, #6748), so only that machine may claim it.
   * Derived from `taskPinnedSql()`, the box-independent fragment `markMachinePinnedHolds`
   * stamps from. Transient: that machine takes it on its next pass, or the reaper, the drain
   * or the offline backstop clears the pin.
   */
  MachinePinned: 'machine_pinned',
} as const;
export const TaskStallKindSchema = z.enum(TaskStallKind);
export type TaskStallKind = z.infer<typeof TaskStallKindSchema>;

/**
 * The order the classifier resolves a task's ONE reason in — most specific and
 * most permanent first, widest and most transient last. A stalled task usually
 * satisfies several predicates at once (an account with no box also has an
 * un-drained area), and a report that listed a task under each of them would
 * make its counts un-addable.
 *
 * ONE array, consumed by the SQL `CASE` and by every consumer that ranks, so the
 * classification cannot be spelled one way in the query and another in a test.
 * Exhaustive by construction: `TASK_STALL_PRECEDENCE.length` is asserted against
 * the enum, and the property — not the count — is what the guard states.
 */
export const TASK_STALL_PRECEDENCE: readonly TaskStallKind[] = [
  TaskStallKind.DependencyFailed,
  // Beside `DependencyFailed` and above `DependencyPending`, which the same row also
  // satisfies (the edge is unreleased): "let it finish" is false about an upstream that
  // has finished, so the narrower cause wins.
  TaskStallKind.DependencyUnreleasable,
  // ABOVE `BlockedOnAnswer`, which the same row also satisfies (its hold raised a blocker
  // naming it): that arm's remedy reads as "answer the blocker" with no word of the held
  // task, and this one names the release. The more specific cause wins.
  TaskStallKind.EnvFaultExhausted,
  TaskStallKind.BlockedOnAnswer,
  // DIRECTLY ABOVE `UpstreamGroupUnmerged` (#6748): a person's decision, released only by
  // Resume. A paused slice usually also has an unmerged upstream, and reporting that would
  // send the reader to merge a pull request that releases nothing.
  TaskStallKind.PlanPaused,
  TaskStallKind.UpstreamGroupUnmerged,
  // DIRECTLY BELOW `UpstreamGroupUnmerged` (slice 06): a row with an unmerged edge and an
  // undeployed one is named by the unmerged cause, which comes first in time.
  TaskStallKind.UpstreamLegUndeployed,
  TaskStallKind.DependencyPending,
  // An operator switched this task's mode OFF: the most specific repo-scoped cause,
  // permanent until the same operator re-enables it, and the one they can clear with
  // one click. Above `RepoNotOnboarded` and the account-wide arms because a row held
  // here stays held after every one of those clears — reporting "not onboarded" or
  // "no box" would send the reader to fix something that does not release it.
  TaskStallKind.ModeDisabled,
  // Repo-scoped and permanent-until-acted, so it sits above the two ACCOUNT-wide
  // arms: a task held here would still be held on an account with boxes and keys,
  // and reporting it as "no box" would send a maintainer to enrol hardware that
  // changes nothing. Below the per-task arms, which are narrower still.
  TaskStallKind.RepoNotOnboarded,
  // Repo-scoped like `RepoNotOnboarded` and below it: that one lifts only on a merge,
  // this one on the first setup that passes — a probe, at most one probe interval
  // away, or at once after a push to the default branch. Above the account-wide arms
  // for the reason `RepoNotOnboarded` is: a box or a key releases none of these rows.
  TaskStallKind.RepoEnvFault,
  // Per-task and self-clearing on a clock: it resumes on its own. Above the account-wide
  // arms because no box or key releases it early — only the window or a redispatch does.
  TaskStallKind.ResumeBackoff,
  // Per-task and self-clearing on a clock, like `ResumeBackoff`: the person's pushes are
  // the wall, and no box or key releases it early.
  TaskStallKind.PrQuietWindow,
  TaskStallKind.NoBoxOnAccount,
  // Below `NoBoxOnAccount` and above `NoConverseCapacity`. It is disjoint from the converse
  // arm by construction (both halves of `noRoleMachineSql` carry the conversational clause),
  // and it needs a box that could place the row, so it is true of an account with no box of
  // its own only when a box reaching from elsewhere could: there the wider arm wins by this
  // slot. It sits in the permanent-until-acted band: nothing releases it until someone turns
  // a switch back on.
  TaskStallKind.NoRoleMachine,
  // BELOW `NoBoxOnAccount` deliberately. Both are true of an account with zero
  // boxes, and there the wider arm carries the ACTIONABLE remedy ("enrol one") --
  // telling that operator to re-pin a box they do not have would send them
  // nowhere. Above the two transient arms because it is permanent until somebody
  // acts: a key pool cools off and an area drains on its own, a fleet pinned
  // `worker` does not.
  TaskStallKind.NoConverseCapacity,
  // Beside `NoConverseCapacity` rather than above or below it, because the two are
  // DISJOINT BY ORIGIN: that one judges interactive work, this one coding work, so no
  // row can satisfy both and their relative order can never decide a report. It sits
  // in the same band for the same reason -- permanent until somebody provisions the
  // service or the repo withdraws the declaration -- and above the two transient arms.
  TaskStallKind.NoServiceCapacity,
  TaskStallKind.NoUsableKeys,
  // An ACCOUNT fact, so above `AreaBusy` (a per-repo, self-clearing one): a row
  // held by both is not waiting for a sibling to release a box, it is waiting for
  // a slot that no release creates while the cap stands. Below `NoUsableKeys`,
  // which is permanent until somebody acts, where this clears by itself the
  // moment a running task finishes — or sooner with a seat or an `ai_devs` grant.
  TaskStallKind.SeatCapped,
  // ABOVE `AreaBusy`, and the rows that were MISLABELLED `area_busy` are the argument,
  // not a preference — the PROPERTY, with no count attached, because cohort membership
  // is decided by a Dragonfly key that no SQL can re-derive (this file's `PoolCooling`
  // member says so). The pool-cooling gate refuses BEFORE the claim, so
  // `markTaskHolds`' `held` write stamps OVER whatever an earlier pass left on the
  // row (`is distinct from`) — a pass that got further has been superseded. Ranked
  // below `AreaBusy` instead, the column would read `pool_cooling` while this report
  // said `area_busy` for the same row, which is the stamped-vs-derived drift
  // `UpstreamGroupUnmerged`' docblock already paid for once. And the report would be
  // wrong on its own terms: the sibling releasing its box hands the row to a claim
  // the cooldown refuses anyway, so "another task is editing this repo" sends a
  // maintainer to free a box that changes nothing.
  //
  // BELOW `SeatCapped` and `NoUsableKeys` for the reason that band is already
  // ordered by: those two are cleared by an ACTION somebody can take (a seat, an
  // `ai_devs` grant, a key), this one only by a clock somebody else owns. A row held
  // by both is waiting on the half a human can move.
  TaskStallKind.PoolCooling,
  // Beside `PoolCooling` and for its reason: a key wall that lifts on a clock (or when
  // a key on the account turns valid), below the arms a human clears by acting.
  TaskStallKind.CredentialCooling,
  // Beside `CredentialCooling` and never beside it on one row (both read the ONE
  // `hold_reason` column): the same kind of wall, on the lane's SELECTED key alone.
  TaskStallKind.SelectionWaiting,
  // Beside `CredentialCooling`, in the same band and for the same reason: a key wall that
  // lifts by itself (here, when a running task ends and frees a slot on the key), below
  // the arms a human must clear by acting, above `AreaBusy` because a sibling releasing
  // its box frees no stream on a saturated key. Its order relative to `CredentialCooling`
  // never decides a report: both read the ONE `hold_reason` column, so no row carries
  // both. Below it anyway, because a cooling key is the harder wall — it serves nothing
  // until its reset, where a saturated one serves the next task the moment a slot frees.
  TaskStallKind.KeySaturated,
  TaskStallKind.AreaBusy,
  // DIRECTLY ABOVE `BoxFaultExcluded` (#6748): transient — its own machine takes it on its
  // next pass, or the reaper, the drain or the backstop clears the pin.
  TaskStallKind.MachinePinned,
  // LAST (#3094): the widest and most transient arm. It lapses on a clock, and any
  // OTHER box may claim the row meanwhile, so a row also held by anything above is
  // really waiting on that cause — reporting the exclusion would hide it.
  TaskStallKind.BoxFaultExcluded,
];

/**
 * How many pull requests the remedy names before it starts counting. Ten is every
 * repo an account has ever held this way, twice over (prod 2026-09-11: one account,
 * two repos), and the overflow is COUNTED rather than dropped — a sentence that
 * quietly names 10 of 30 is a to-do list the reader under-fills.
 *
 * It is also what BOUNDS the field now that each reference carries its repository.
 * A GitHub full name is at most 39 + 1 + 100 characters by GitHub's own limits, so
 * ten references bound the whole sentence at 2,109 characters — measured, with ten
 * maximum-length names and the overflow and mixed-case clauses both present —
 * however many repos an account holds. The live shape is 516 (one repo, prod
 * 2026-09-11), and the notification's own `BODY_MAX` is 1,000, which only the
 * aggregated `queueStallReport` sentence can approach and the one-repo push cannot.
 * That is the objection that kept repo names out of this sentence, answered by the
 * cap that was already here rather than by dropping the identity a reference needs.
 */
const REMEDY_PR_MAX = 10;

/**
 * WHAT the caller knows about the repos this hold covers.
 *
 * A SET, not one number, because the two readers of this sentence aggregate
 * differently and both have to be right. The onboarding-wait notification speaks
 * about ONE repo; `queueStallReport` returns ONE row per {@link TaskStallKind} over
 * every held repo of an account — and prod on 2026-09-11 says that row is already
 * plural in the only place it exists: the single account holding
 * `repo_not_onboarded` holds SIX rows across TWO repos, `developerz-ai/ggrts`
 * (pull request #19, open since 2026-09-08) and `developerz-ai/ggezgame.com` (no
 * pull request — its pass ended `blocked` on `no_checkout`). A signature that took
 * `number | null` could only have named #19 by pretending the second repo did not
 * exist, or named nothing by refusing to choose. It printed nothing, which is how
 * this sentence came to name a branch and never a pull request.
 *
 * AND A NUMBER IS NOT AN IDENTITY. A pull request number is REPOSITORY-scoped, so
 * two held repos can each be on `#19` and both have to merge. Taking bare numbers
 * made that shape unrepresentable in three separate ways at once — the pair
 * deduplicated to one entry in `queueStallReport`'s `Set`, the singular arm then
 * said `MERGING IT` for two owed merges, and a maintainer sent to merge `#19` could
 * not tell which `#19`. Each entry therefore carries the repo it belongs to, and the
 * sentence renders `owner/repo#N`, which is the cross-repo reference GitHub itself
 * resolves. The earlier reasoning — that the repo is a per-repo fact keyed elsewhere
 * (`PlanProjectionGroup.heldOn.pullRequestNumber`) and that N names in one string is unbounded — does not
 * survive two repos sharing a number: the per-repo pointer is a different surface,
 * and the bound is {@link REMEDY_PR_MAX}, which was already here.
 */
export type RepoOnboardingRemedyInput = {
  /**
   * The onboarding pull requests open across the held repos, each with the repo it
   * belongs to. EMPTY is a real answer — no pass has linked one yet — and the
   * sentence then says what WILL mint one rather than trailing off.
   *
   * Rendered sorted by repo then number, so one hold reads the same on every call;
   * the caller is not asked to pre-sort. Entries are assumed DISTINCT by
   * `(repoFullName, number)` — both producers build them from a per-repo map, so a
   * duplicate is unrepresentable upstream and de-duplicating here would be handling
   * for a case that cannot arrive.
   */
  readonly pullRequests: readonly {
    /** `owner/repo`, as `tasks.repo_full_name` holds it. */
    readonly repoFullName: string;
    /** The pull request number WITHIN that repo — meaningless without it. */
    readonly number: number;
  }[];
  /**
   * Does at least one held repo have NO onboarding pull request? Separate from
   * `pullRequests.length`, because the mixed case is the live one and the two
   * facts have different remedies: merging clears the repos that have a PR and
   * does nothing at all for the ones that do not. Collapsing them would tell a
   * maintainer that one merge releases everything.
   */
  readonly anyWithoutPullRequest: boolean;
};

/**
 * WHAT CLEARS {@link TaskStallKind.RepoNotOnboarded}, naming the pull requests that
 * are already open — the ONE spelling of that sentence in the tree, and a function
 * rather than a constant because the actionable half depends on facts the enum
 * cannot carry.
 *
 * THE DEFECT. The `dz/onboarding` branch name was all this remedy ever said.
 * Measured on prod 2026-09-11: `developerz-ai/ggrts` had five coding tasks held
 * since 2026-08-31 while pull request **#19** — the onboarding PR, open since
 * 2026-09-08 — sat waiting for a merge, and nothing the maintainer could read
 * named it. Being told to wait for a pass that already ran, and to merge a branch
 * whose pull request is already open, is indistinguishable from the platform not
 * having noticed.
 *
 * BOTH SURFACES RENDER THIS, and that is the point of it being a function:
 * `queueStallReport` (`@developerz/db`) resolves the numbers for the repos it
 * reports this kind on, and `planOnboardingWait`
 * (`apps/worker/src/notifications/onboarding-wait.ts`) resolves the one for the
 * repo it is telling somebody about. Both go through the SAME resolver
 * (`loadOnboardingPullRequests`), so `task stalls`, the plan projection and the
 * push cannot name different pull requests for one hold.
 *
 * {@link TASK_STALL_REMEDY} holds the nothing-known form, so the static record a
 * reader sees and the resolved sentence can never drift into two descriptions of
 * one hold.
 */
export function repoNotOnboardedRemedy(input: RepoOnboardingRemedyInput): string {
  // The half every form ends on, mid-sentence after a semicolon.
  //
  // TWO DOORS, AND THE VERB LEADS (#4607). This one string is served on BOTH
  // surfaces — `task stalls` renders it, and the MCP `repo_onboarded` rung IS it
  // (`packages/mcp/src/tools.ts`, which reads it from here rather than writing a
  // second copy) — and it used to offer only `PATCH /v1/repos/:owner/:repo`: an
  // HTTP call the MCP caller it was handed to cannot make, with nothing beside
  // it. Same shape as `missingScopeMessage`: the door on the reader's own
  // surface first, the REST route after it for the reader who has one.
  //
  // Graded per CLAUSE, not per paragraph, by `onboarding-guidance.test.ts` §
  // "every clause instructing an HTTP call names a verb, a URL or a role" — the
  // `project setup` two clauses upstream never rescued this one, and a
  // whole-string check was green while this sentence was a dead end.
  const declareInstead =
    'to declare the repo already AI-first instead, `project set_ai_first` stamps it from here and `PATCH /v1/repos/:owner/:repo` with `ai_first: true` is the same door over REST — either releases the held coding work with no pass at all. Issue triage, PR review and report/ops tasks on the same repo are unaffected';
  const lead = 'this repo has never been onboarded, so coding work on it is held';
  // The pass has not run, or ran and opened nothing: there is no number to name, so
  // the sentence says what mints one. `anyWithoutPullRequest` adds nothing here —
  // it is already the whole population.
  if (input.pullRequests.length === 0) {
    return `${lead} — and the pass that clears it is minted FOR you: \`cron.onboarding_backfill\` (#3481) mints one within the hour for any repo whose coding work is held this way. It ends by opening the \`${ONBOARDING_BRANCH}\` pull request, and MERGING THAT is the step that is yours — it is what stamps the repo AI-first and releases this work. To skip the wait, \`project setup\` mints the pass now; ${declareInstead}`;
  }

  const shown = [...input.pullRequests]
    .sort((a, b) => a.repoFullName.localeCompare(b.repoFullName) || a.number - b.number)
    .slice(0, REMEDY_PR_MAX);
  const dropped = input.pullRequests.length - shown.length;
  // A cap is never silent (`docs/idea/principles.md`): the count rides the sentence
  // rather than the list simply ending.
  const overflow = dropped > 0 ? ` (+${String(dropped)} more)` : '';
  // `owner/repo#N`, never a bare `#N`: the number is repo-scoped, so two held repos
  // on `#19` are two references and two merges. This is GitHub's own cross-repo
  // spelling, which resolves wherever the sentence is rendered as markdown.
  const refs = shown.map((pr) => `${pr.repoFullName}#${String(pr.number)}`).join(', ');
  const plural = shown.length > 1 || dropped > 0;
  const merge = plural
    ? `pull requests ${refs}${overflow} (\`${ONBOARDING_BRANCH}\`) are open, and MERGING THEM is the step that is yours`
    : `pull request ${refs} (\`${ONBOARDING_BRANCH}\`) is open, and MERGING IT is the step that is yours`;
  // THE MIXED CASE, said out loud, because it is the only one prod has: merging
  // releases the repos that have a pull request and changes nothing for the rest.
  // A sentence that stopped at the merge would promise a clear queue.
  const rest = input.anyWithoutPullRequest
    ? ' Not every held repo has one — the rest are still waiting on a pass, which `cron.onboarding_backfill` (#3481) mints within the hour, or `project setup` mints now.'
    : '';
  return `${lead} — and the pass that clears it has ALREADY RUN: ${merge}. Merging stamps those repos AI-first and releases their work, and nothing else has to happen first.${rest} To finish here instead, ${declareInstead}`;
}

/**
 * What the dependency sweep breaks and what it leaves, in ONE spelling: the
 * `dependency_pending` remedy below and `task outcome`'s DEPENDENCY CYCLE sentence
 * (`@developerz/mcp`) both quote it, so a change to the rule (`breakDependencyCycles`,
 * `@developerz/db`) is one edit. Starts lower-case, ends without a full stop.
 */
export const DEPENDENCY_CYCLE_SWEEP_RULE =
  "the platform's dependency sweep breaks a cycle through a plan-group edge (removing the `depends_on` edges that contradict the plan's group order) and a `depends_on`-only cycle that has an older-to-newer edge (the shape a regroup's heal writes; a `task depend` edge of that shape on the same cycle can be removed too); any other cycle ends only when one side is cancelled (`task cancel`)";

/**
 * WHAT CLEARS IT — one sentence per kind, addressed to the person who can act.
 * `Record` over the closed enum, so a kind cannot ship with a count and no
 * remedy: a reason a reader cannot act on is the eleven-day stall with better
 * telemetry.
 *
 * Prose, not a code, because the audience is a human or an agent reading the
 * report; the machine half is the `kind` beside it.
 */
export const TASK_STALL_REMEDY: Record<TaskStallKind, string> = {
  [TaskStallKind.DependencyFailed]:
    'a task this one depends on died and will never satisfy it — re-author the plan; retrying this row cannot help, because a retry keeps the same dead reference',
  [TaskStallKind.BlockedOnAnswer]:
    'a human answer is owed — answer the blocker (`blocker answer`). That does NOT restart the stopped run: it files a raw task carrying the ask and the answer, which triage then decides on the account’s own key',
  [TaskStallKind.PlanPaused]:
    "a person paused this task's epic, which holds its waiting slices with the running ones — resume the epic and they are claimed again on the next dispatch pass; nothing else releases them",
  [TaskStallKind.MachinePinned]:
    'this task was paused on a machine that saved its work there, and it resumes only on that machine — it is claimed on that machine’s next dispatch pass. If that machine went offline, was drained or was revoked, the platform clears the pin and any machine may take it',
  [TaskStallKind.UpstreamGroupUnmerged]:
    'an upstream plan group has not merged — merge (or cancel) its pull request; the stacked-PR gate holds every downstream group until it does',
  [TaskStallKind.UpstreamLegUndeployed]:
    'an upstream plan group merged, and this group waits for that merge to be LIVE in the environment its edge names — it is claimed once a deploy of the merge commit to that environment reports success. If the newest deploy there failed, fix and redeploy; if none was ever reported, check that the deploy emits a GitHub deployment status for that environment',
  [TaskStallKind.DependencyPending]: `an upstream task has not released it (merged, or finished with no pull request) — let it finish, or cancel it (\`task cancel\`) if it is not wanted. If that upstream waits back on this task (through \`depends_on\` or a plan group’s order) it is a dependency CYCLE no box can ever break: ${DEPENDENCY_CYCLE_SWEEP_RULE}. \`task outcome\` on the held task names each dependency and flags the cycle`,
  [TaskStallKind.DependencyUnreleasable]:
    "an upstream task can never release it by itself — it finished without the merge the edge waits for. If that upstream is a PR chore (a babysit round, a review) or a task a regroup superseded, the platform's dependency sweep rewrites the edge within ~10 minutes: to the task that owns the chore's pull request, or to the replacements (and drops an edge that would close a cycle); a dependency that is then dead fails this task `dependency_failed`. If it is a task that completed without the pull request it promised, `task redispatch` it, or `task cancel` this one if it is not wanted. `task outcome` on the held task names each dependency",
  [TaskStallKind.NoBoxOnAccount]:
    'this account has no live box — enrol one (`box add`), or accept donated capacity; nothing can claim the work until one exists',
  [TaskStallKind.NoServiceCapacity]:
    "no online box can serve this task's capabilities — either the repo's `.maintainer.yml` declares a service its own verify gate needs (`services:`) and no box can reach one, or the task itself requires a capability token (`requires` in its envelope) that no box advertises. Either way the fix is the same: bring online a box that provides what is missing, or drop the requirement if nothing needs it. When the gap is the `services:` declaration, run the service on a box (a Redis or Postgres on loopback is enough — `attach-vm.sh --unit-only` starts only Redis on a converge, never Postgres), and when the repo's own compose file brings the service, drop the `services:` declaration instead. No restart is needed: the box re-probes what it can reach and re-adverts within `CAPABILITY_REFRESH_MS` (#3065). Until then the work is held rather than claimed and discarded",
  // FOUR WAYS A KEY LEAVES THE POOL, and this sentence named three of them until
  // 2026-09-12. The fourth is a key that is `valid`, verified and unfenced whose
  // MODEL A VERIFY parked (`byok_key_models.active = false`, written only by
  // `markKeyValidParkingModel` in `@developerz/db`, off the structured-output
  // probe's verdict — never a person, #4366 F4): `refuseKeyModel`
  // (`value-objects/llm-credential/models.ts`) drops it from every resolve, so it is
  // out of the pool exactly like a cooled one — and the owner who reads "cooled,
  // invalid or exhausted", opens the keys page and finds every key green concludes
  // the platform is lying to them. Measured on prod 2026-09-12: one account, both of
  // its keys, their only catalogue row parked, 68 resolutions in a partial day.
  // A park alone does NOT reach this kind (an all-parked pool leaves
  // `createUsableKeyResolver` unbound, so the row is claimed rather than held); the
  // MIXED pool does — one key cooling, one parked — and there the missing name is
  // the difference between "wait" and "act".
  //
  // THE REMEDY SENTENCE NAMED A ROUTE THAT WRITES NOTHING until #4366: `GET
  // /v1/billing/keys/:id/models` is `listModelsForCapturedKey` (`@developerz/billing`)
  // — a read plus a provider GET, no row touched — so "re-open the key … refreshes
  // the catalogue" told an owner to perform a no-op. What actually un-parks the model
  // is a promote through `markKeyValid`, and since #4366 the platform reaches it by
  // itself: the park stamps `verification_retry_at` 24h out and
  // `cron.byok_reverify`'s parked-shut arm claims the key — bounded by
  // `MODEL_PARK_MAX_CONSECUTIVE_PARKS` (3 consecutive parks of the pair), past which
  // nothing is scheduled. So waiting is the remedy while the machine is still
  // looking, and re-tagging a model the provider serves is the remedy after that.
  [TaskStallKind.NoUsableKeys]:
    'every BYOK key on this account is out of the pool — cooled, invalid, exhausted, or valid with the model it names parked in its own catalogue. Add or repair a key; a parked model needs nothing from you at first, because the platform re-probes such a key about once a day for a few days (`cron.byok_reverify`) and switches the model back on as soon as it answers — if it is still parked after that, nothing further is scheduled and the fix is yours: tag the key with a model the provider still serves',
  // #3965: "declare non-overlapping `files_touched`" used to name the escape
  // from the WAITING side alone, and that is unreachable exactly when it
  // matters most — `provablyDisjointSql` needs BOTH sides to declare a
  // non-empty, non-overlapping set, so a holder that has declared NOTHING
  // cannot be proven disjoint from anything the waiter declares. Measured on
  // prod 2026-09-17: seven CI-green, dz-APPROVED PRs sat behind exactly that
  // holder while every one of their own follow-ups declared its files. The
  // sentence now points at the holder this report's own `detail` names,
  // rather than promising an escape only the reader's side controls.
  [TaskStallKind.AreaBusy]:
    "another task is already editing this repo — it clears when that task releases its box, or sooner if the holder named in this reason's `detail` ALSO declares a `files_touched` set that does not overlap yours. An undeclared holder cannot be proven disjoint from anything, so declaring your own files does not unblock it by itself",
  [TaskStallKind.NoConverseCapacity]:
    'this is an interactive session (a scout plan or a project setup) and no box on this account can hold a mailbox -- only a `universal` box can. Pin one with `DZ_RUNNER_MODE=universal` in its `/etc/dz-runner/env`, or unset the variable entirely (an unpinned box boots `universal`), then restart the runner. A box pinned `worker` runs coding work and can never take this row',
  // THE REMEDY THAT NEARLY SAID "NOTHING TO DO" AND STOPPED THERE. An owner reading a
  // stall reason is looking for the lever; usually this hold has none — the reset
  // belongs to their upstream router, not to us and not to them. So it names the bound,
  // and names the CONSTANT rather than quoting a duration, because the number lives in
  // `apps/worker` and this package imports nothing: a figure copied across that
  // boundary is a promise that drifts the day the cap moves.
  //
  // AND THEN IT NAMES THE CASE WHERE THE HOLD DOES NOT LIFT BY ITSELF, because the
  // stamp and the cooldown do not expire together ({@link TaskHoldReason.PoolCooling}):
  // the column is cleared by a later dispatcher pass that reaches the gate, so an
  // account whose boxes all went offline or stayed busy keeps a dead `pool_cooling`
  // after its record is long gone — and no other hold writer can correct the row,
  // because every one of them but two stamps only over a NULL `hold_reason` (or a lapsed
  // `error_issue_pending`, or, `markAreaHolds` only, a lapsed key park; never a
  // `pool_cooling`). A remedy
  // that said only "nothing to do, it lifts by itself" would be the confident wrong
  // answer this member was minted to remove, aimed the other way.
  [TaskStallKind.PoolCooling]:
    "this account's shared upstream router pool refused a box for a capacity limit and quoted a reset, so coding claims for the account step aside until it passes. Normally there is nothing to do and no box, seat or top-up shortens it: the hold runs to the router's own reset (bounded by `POOL_COOLDOWN_CAP_SEC`), after which ONE box goes back to the pool to find out whether it recovered while the others keep waiting, and the fleet resumes as soon as that one gets through. IF IT HAS NOT MOVED IN FAR LONGER THAN THAT, the cooldown is over and this label is stale rather than true — it is cleared by the next dispatch pass that runs for this account, and no pass runs while every box is offline or busy. Check which boxes are live (`box list`, or the Fleet page) and restart the dz-runner on any that are down; the next pass then clears this and the work is claimed. If the same pool keeps quoting a reset, the durable fix is upstream, and the router's own refusal says which one: it reports each pool account as cooling, as out of credit and needing a top-up, or as needing a human to re-enable it, and the reset it quotes clears only the first. Read it on the held task's run timeline (`task outcome` carries it verbatim), then raise the pool's limit, top up the account it names, re-enable the one it says a human disabled, or point this account's keys at a provider that is not rationing it",
  [TaskStallKind.CredentialCooling]:
    "every key in this account's pool that can run this task is cooling — rate-limited by its provider, or out of quota until a reset the provider stated — so the task is held until `hold_until` instead of starting a run that would be refused. It is claimed again at that instant, or sooner when a key on the account turns valid. Nothing to do if the wait is short; if it keeps recurring, the account needs another key that serves this lane (a second provider, or a plan with more concurrency), and `task outcome` on a recently refused run quotes the provider's own refusal",
  // THE SELECTION, said first: the reader's other keys are fine, and the one lever that
  // moves this at once is the page that made the choice.
  [TaskStallKind.SelectionWaiting]:
    "this task's agent is set on the Agents page to run on one selected model key, and that key cannot run right now — it is cooling after a provider refusal, out of quota until a reset the provider stated, or has refused several runs in a row — so the task waits for THAT key until `hold_until` instead of running on another one. Your other keys are not affected and this is not an account outage. To run it now, change that agent's model on the Agents page (or set it back to Automatic): the change takes effect at once and the waiting tasks are picked up on the next dispatch pass. Otherwise it is tried again at `hold_until`, or sooner when a key on the account turns valid; `task outcome` on a recently refused run quotes the provider's own refusal",
  // NO KEY IS BROKEN, and the sentence says so first: an owner reading "held" beside a
  // key reflexively opens the keys page, finds every key green, and concludes the
  // platform is lying. The wall is the account's own running work on the key's plan.
  // TWO MEMBERS FOLD HERE (#6704): `key_saturated` (the keys are physically full) and
  // `key_reserved` (a free slot is held for higher-priority work). The sentence holds for
  // both, and the row's own `hold_reason` says which.
  [TaskStallKind.KeySaturated]:
    "this task waits for a slot on a model key: the keys in this account's pool that can run it are already running as many concurrent model streams as their plan allows (one z.ai coding subscription runs one PR at a time unless you declare more), or a free slot is being held for higher-priority work such as finishing an open pull request. Nothing is broken and nothing is lost: if its keys were full it is claimed as soon as one of them has room, and if a free slot was held for higher-priority work it is re-checked after a short wait; either way it is re-checked at `hold_until` at the latest, and parked again if its keys are still full then. To run more at once, declare a higher capacity if the plan really allows it (`billing key_capacity_set`), or add another key that serves this lane",
  [TaskStallKind.SeatCapped]:
    'this account is at its AI-dev cap — every seat is running work, so this row waits for a slot to free. To run more in parallel, add a seat (`billing`), or ask an operator for an `ai_devs` grant',
  [TaskStallKind.NoRoleMachine]:
    'every machine that could run this task has its role switched off — turn it back on for one machine (`agent_mode machine_clear`, or `DELETE /v1/agent-modes/machine/<runner>/<mode>`); nothing claims the work until one does',
  [TaskStallKind.ModeDisabled]:
    "this task's agent mode (scout, worker, ci_fixer, reviewer or devops) is switched off for its repository or organization — re-enable it in the repository's or the organization's agent mode settings (a repository setting overrides the organization's). The task is held, not failed, and is claimed the moment the mode is back on",
  [TaskStallKind.BoxFaultExcluded]: `a box is kept off this task for a fault of its own, one of three. Either it refused the task because its own checkout of the repo's default branch failed verify before any change was applied, so that box sits this task out for ${String(BOX_FAULT_EXCLUSION_MINUTES)} minutes while any other box may claim it: nothing to do if another box is online; if every box refuses, the repo's default branch itself is red — fix it there, because after three refusals in a row the task ends. Or its runner build could not decode the task's assignment, and the task is held, with no attempt charged, for a box on a newer runner release than the one that refused: update the account's boxes (\`box update\`), and it is claimed the moment one is online (the refusing release is on the task's \`task.held\` audit row). Or the repository's own check ran out of memory at a box's per-task memory cap, and the task is held for a box whose cap is larger while no such box is online: connect a box with more RAM, and it is claimed the moment that box is online (\`task stalls\` names the cap it needs and the largest online)`,
  // The NOTHING-KNOWN form: this record is keyed by kind alone and has no repo to
  // resolve against, so it holds the sentence a caller renders when it has not
  // asked. `queueStallReport` and the onboarding-wait notification both ask.
  [TaskStallKind.RepoNotOnboarded]: repoNotOnboardedRemedy({
    pullRequests: [],
    anyWithoutPullRequest: true,
  }),
  // The nothing-known form; `queueStallReport` renders {@link repoEnvDownRemedy} over
  // the faults it found, which names the signature.
  [TaskStallKind.RepoEnvFault]: repoEnvDownRemedy([]),
  // The nothing-known form; `queueStallReport` names each parked task (`resumeBackoffRemedy`).
  [TaskStallKind.ResumeBackoff]: resumeBackoffRemedy([]),
  [TaskStallKind.EnvFaultExhausted]: ENV_FAULT_HOLD_REMEDY,
  [TaskStallKind.PrQuietWindow]:
    'a person is pushing to this pull request, so the babysit round waits until nobody has pushed for the quiet window instead of working a head that is about to move. Nothing to do: it starts on its own at `hold_until`; if the pushes are done and you want it now, `task redispatch` runs it at once',
};

/** One open repo-environment fault, as the stall report names it. */
export type RepoEnvFaultView = {
  /** `owner/repo`. */
  readonly repoFullName: string;
  /** The normalised refusal the boxes shared (`repoSetupFailure`'s signature). */
  readonly signature: string;
  /** How many distinct boxes refused with it when the fault opened. */
  readonly boxes: number;
  readonly openedAt: Date;
  /** When the sweep next lets one held task through as a probe. */
  readonly nextProbeAt: Date;
};

/**
 * WHAT CLEARS {@link TaskStallKind.RepoEnvFault}: the repo's setup has to pass again,
 * and the sentence names the signature so the reader knows WHAT to fix without opening
 * a run. Nothing to redispatch: held rows are released by the platform the moment one
 * setup passes, and the tasks the outage already failed were re-queued when it opened.
 */
export function repoEnvDownRemedy(faults: readonly RepoEnvFaultView[]): string {
  const how =
    "the repo's own `bin/setup` is failing the same way on every box, so the platform parks the work that runs setup instead of spending its attempts. Fix the cause in the repo (or wherever every box shares it — a registry, a base database) and push to the default branch: the push releases ONE held task at once as a probe, the periodic probe does the same when the fix lands elsewhere, and the first setup that passes releases every held task. Nothing to redispatch — the tasks the outage already failed were re-queued when it was detected";
  if (faults.length === 0) return how;
  const named = [...faults]
    .sort((a, b) => a.repoFullName.localeCompare(b.repoFullName))
    .slice(0, REMEDY_PR_MAX)
    .map(
      (f) =>
        `${f.repoFullName}: \`${f.signature}\` on ${String(f.boxes)} boxes since ${f.openedAt.toISOString()} (next probe ${f.nextProbeAt.toISOString()})`,
    );
  const more = faults.length - named.length;
  return `${how}. Failing now — ${named.join('; ')}${more > 0 ? `; and ${String(more)} more` : ''}`;
}

/**
 * WHY one box-held task holds one waiting row — the arm of the #513 gate the pair
 * fails (`areaHoldsSql`, `@developerz/db`), named so the remedy can say who can act:
 *
 *   `not_disjoint`       an EDITING holder the waiter cannot be proven disjoint from.
 *                        Either side declaring a non-overlapping `files_touched`
 *                        frees it, and an undeclared holder cannot be proven disjoint
 *                        from anything. A `pr_babysit` waiter is the exception: only a
 *                        writer on its OWN pull request holds it (same branch), and
 *                        that hold ends when the writer does.
 *   `same_pr_review`     a review waiting behind the running review of its own pull
 *                        request (one active review per PR).
 *   `starved_writer_ahead` a PENDING editing task, older than the waiter and held by
 *                        `area_busy` past the fairness threshold, of equal or higher
 *                        `AdmissionRank`, that the waiter cannot be proven disjoint
 *                        from. The waiter yields so the
 *                        area drains and the starved writer runs (`areaFreeSql`'s
 *                        fairness arm, `@developerz/db`). Nothing is owed while the
 *                        drain runs; the one move is the starved writer's own: it is
 *                        not starved while a PR fix round holds it (#6707), so each
 *                        round resets its turn, and declaring a `files_touched` set
 *                        that does not overlap the rounds' lets it run beside them.
 *
 * There is no arm for a NON-EDITING holder (a review, a scout, a devops report)
 * holding an editing waiter: it edits no tree, so it conflicts with no writer,
 * declared or not (2026-09-24 — it used to hold an undeclared one).
 */
export const AreaHoldKind = {
  NotDisjoint: 'not_disjoint',
  SamePrReview: 'same_pr_review',
  StarvedWriterAhead: 'starved_writer_ahead',
} as const;
export type AreaHoldKind = (typeof AreaHoldKind)[keyof typeof AreaHoldKind];

/** One named holder of an `area_busy` wait — see {@link QueueStallDetail}. */
export type AreaHolder = {
  /** The task holding this repo's area. */
  readonly taskId: TaskId;
  /** `owner/repo` — which of the account's held repos this holder is on. */
  readonly repoFullName: string;
  /**
   * The holder's OWN declared blast radius. EMPTY means UNDECLARED, never "touches
   * nothing" — the same reading `filesTouchedSql` and `provablyDisjointSql` take, so
   * this detail and the #513 gate's own refusal cannot tell two different stories
   * about the same array.
   */
  readonly filesTouched: readonly string[];
  /** Since when this box has held the area (its current claim's last transition). */
  readonly heldSince: Date;
  /** The holder's origin — a `pr_review` cannot declare files, and the remedy says so. */
  readonly origin: TaskOrigin;
  /** Which arm of the gate this holder fails some waiting row on. */
  readonly holds: AreaHoldKind;
};

const AREA_HOLD_REMEDY: Record<AreaHoldKind, string> = {
  [AreaHoldKind.NotDisjoint]:
    "an editing task holds this repo — it clears when that task releases its box, or sooner if the holder named in this reason's `detail` ALSO declares a `files_touched` set that does not overlap yours. An undeclared holder cannot be proven disjoint from anything, so declaring your own files does not unblock it by itself",
  [AreaHoldKind.SamePrReview]:
    'a review is waiting behind the running review of its own pull request (one active review per PR) — nothing to do; it claims when that review ends',
  [AreaHoldKind.StarvedWriterAhead]:
    'an older editing task has waited on this repo past the fairness threshold, so newer overlapping work yields until the running tasks finish and it runs — nothing to do; this claims after it, but if PR fix rounds keep holding it, each one lifts this hold and resets its turn; declaring a files_touched set that does not overlap theirs lets it run beside them',
};

/**
 * The `area_busy` remedy for the holders a report actually named: one clause per
 * {@link AreaHoldKind} present, in that enum's order, so the sentence tells whoever
 * reads it which side can act. Empty ⇒ the nothing-known {@link TASK_STALL_REMEDY}.
 */
export function areaBusyRemedy(holders: readonly AreaHolder[]): string {
  if (holders.length === 0) return TASK_STALL_REMEDY[TaskStallKind.AreaBusy];
  const clauses = Object.values(AreaHoldKind).flatMap((kind) =>
    holders.some((h) => h.holds === kind) ? [AREA_HOLD_REMEDY[kind]] : [],
  );
  return clauses.join('; and ');
}

/**
 * What a reason knows BEYOND its kind.
 *
 * A discriminated union rather than a bag of optional fields, and optional on the
 * reason rather than universal, because the kinds do not have the same amount to
 * say: `no_service_capacity` knows exactly which tokens are unmet, while
 * `area_busy` has nothing to add that its kind does not already carry. Forcing a
 * value on every kind would mean inventing one for most of them, and an invented
 * detail is worse than an absent one — a reader cannot tell it from a real answer.
 *
 * ABSENT, NEVER EMPTY. `detail === undefined` means "this kind has nothing more to
 * say"; a present detail always carries something. An empty array here would read
 * as "we looked and found no missing services", which for a hold whose whole
 * meaning is a missing service is a contradiction the caller would have to
 * unpick (#2929).
 */
export type QueueStallDetail =
  | {
      /**
       * The `service:*` capability tokens this account's boxes do not advertise —
       * exactly the subset {@link TaskStallKind.NoServiceCapacity} is holding for.
       * Sorted, and never empty: the kind cannot be true without one.
       */
      readonly kind: 'missing_services';
      readonly tokens: readonly string[];
    }
  | {
      /**
       * WHO is holding {@link TaskStallKind.AreaBusy} — named rather than left to
       * a DB query, closing the gap the 2026-09-17 incident (#3965) exposed: an
       * operator reading "another task is already editing this repo" had no way
       * to see WHICH task, or whether it had declared anything, without leaving
       * the report.
       *
       * PLURAL, deliberately, and not a convenience: {@link QueueStallReason} is
       * ONE row aggregated over EVERY held repo of the account (this file's own
       * `RepoOnboardingRemedyInput` docblock names the exact defect a singular
       * shape caused there — two repos both sitting on pull request `#19`
       * collapsed into one entry and told a maintainer to perform one of two
       * owed merges). A holder here is the same shape of fact, keyed by repo
       * instead of by PR number, and would drift into the identical bug the
       * moment an account held more than one repo's area at once.
       *
       * One entry per task that ACTUALLY holds some waiting row — the pair fails
       * the gate's own relation (`areaHoldsSql`, `@developerz/db`), not merely
       * "the oldest box on the repo", which named a provably-disjoint task on a
       * repo two boxes shared (2026-09-18). Ordered by repo, then oldest hold
       * first. ABSENT, never empty, matching this type's own rule:
       * a resolve that finds no live holder (the area cleared between the two
       * reads) says nothing rather than guessing.
       */
      readonly kind: 'area_holders';
      readonly holders: readonly AreaHolder[];
    }
  | {
      /**
       * WHICH repo-environment faults hold {@link TaskStallKind.RepoEnvFault} — the
       * shared setup signature and when the next probe runs, so the reader fixes the
       * right thing without opening a run. One per held repo; never empty.
       */
      readonly kind: 'repo_env_faults';
      readonly faults: readonly RepoEnvFaultView[];
    }
  | {
      /**
       * WHICH tasks are parked on {@link TaskStallKind.ResumeBackoff}: when each resumes,
       * which cycle it is on and the gate-failure count its saved work ended on.
       */
      readonly kind: 'resume_backoff';
      readonly parked: readonly ResumeBackoffView[];
    }
  | HoldWindowDetail;

/** One row of the composed report: a reason, how much it holds, since when, and the fix. */
export type QueueStallReason = {
  readonly kind: TaskStallKind;
  /**
   * Pieces of held WORK this reason accounts for — a pending task on every kind
   * but {@link TaskStallKind.BlockedOnAnswer}, which also counts the run behind
   * an open blocker no pending row carries (a scout ask holds no task by
   * design). One per piece either way, so the numbers still add up.
   */
  readonly tasks: number;
  /**
   * How long this reason has been true for someone: the oldest held task's
   * `created_at`, or — for an ask no pending row carries — the oldest
   * `blockers.asked_at`, which is when the human started owing the answer.
   */
  readonly oldestWaitingSince: Date;
  /** {@link TASK_STALL_REMEDY} for {@link kind}, carried so a consumer renders one object. */
  readonly remedy: string;
  /**
   * The fact this reason knows and its kind cannot say — present only for the
   * kinds that have one ({@link QueueStallDetail}), absent for the rest.
   *
   * `no_service_capacity` is the first: the predicate that decides the hold walks
   * the repo's required `service:*` tokens against every box's adverts, so the
   * unmet subset falls out of a filter already running. Before this the operator
   * read the kind, then opened `.maintainer.yml` to find out whether the gap was
   * `redis` or `postgres` — a hunt over a value the query had in hand (#2929).
   */
  readonly detail?: QueueStallDetail;
};

/**
 * The answer to *"why is nothing moving"*, per account, in one read.
 *
 * `reasons` is RANKED by how long the reason has been true (oldest first, then
 * by how many tasks it holds) rather than by a hand-written severity table:
 * "which of these has been silently true the longest" is the question the
 * eleven-day stall went unanswered on, and it is data rather than taste.
 *
 * `queued` is the anti-vacuity half and it is a SEPARATE field on purpose. When
 * the queue is genuinely just busy, `reasons` is empty and `queued` carries the
 * depth — so an empty `reasons` means "nothing is held", never "we did not
 * look". A caller that renders only `reasons` still cannot show a green board
 * over a deep queue, because `queued.tasks` is right there.
 */
export type QueueStallReport = {
  /** Every reason holding at least one piece of work, oldest-first. Empty ⇒ nothing is held. */
  readonly reasons: readonly QueueStallReason[];
  /** Pending tasks that pass every gate and are simply waiting for a free box. */
  readonly queued: {
    readonly tasks: number;
    /** Oldest merely-queued task, or null when there are none. */
    readonly oldestWaitingSince: Date | null;
  };
};
